import { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Translate from "../../../utils/Translate";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import {
  Box,
  Checkbox,
  Chip,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { getCurrentInputFormat, getCurrentDateMask } from "../../../utils/dateTimeFormat";
import { DatePicker } from "@mui/x-date-pickers";
import { accessKeys, hasSomeAccess } from "../../../utils/userAccess";
import AutocompleteSearchField from "../../AutocompleteSearchField";
import ActorAutocompleteOption from "../../ActorAutocompleteOption";
import { useMemo } from "react";
import dayjs from "dayjs";

const AddOrUpdateMarketNote = ({ actor, type, existingMarketNote, onClose, onSuccess }) => {
  const api = useApi();

  const [isLoading, setIsLoading] = useState(false);

  const [ePortUsers, setEPortUsers] = useState([]);
  const [marketNoteLabels, setMarketNoteLabels] = useState([]);

  const [selectedMarketNoteLabelIds, setSelectedMarketNoteLabelIds] = useState([]);

  const [marketNoteId, setMarketNoteId] = useState("");
  const [textNote, setTextNote] = useState("");
  const [isDone, setIsDone] = useState(null);
  const [notificationDate, setNotificationDate] = useState(null);

  const [company, setCompany] = useState(null);
  const [contactActor, setContactActor] = useState(null);
  const [responsibleEPortUserId, setResponsibleEPortUserId] = useState("");

  const [oldSelectedMarketNoteLabelIds, setOldSelectedMarketNoteLabelIds] = useState([]);
  const [oldMarketNoteId, setOldMarketNoteId] = useState("");
  const [oldTextNote, setOldTextNote] = useState("");
  const [oldIsDone, setOldIsDone] = useState(null);
  const [oldNotificationDate, setOldNotificationDate] = useState(null);
  const [oldCompany, setOldCompany] = useState(null);
  const [oldContactActor, setOldContactActor] = useState(null);
  const [oldResponsibleEPortUserId, setOldResponsibleEPortUserId] = useState("");

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    slotProps: {
      paper: {
        style: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
          width: 250,
        },
      },
    },
  };

  useEffect(() => {
    if (!existingMarketNote) {
      if (actor && type === "person") {
        setContactActor({ actorId: actor.actorId, actorName: actor.actorName });
        setOldContactActor({ actorId: actor.actorId, actorName: actor.actorName });
      } else if (actor && type === "company") {
        setCompany({ actorId: actor.actorId, actorName: actor.actorName });
        setOldCompany({ actorId: actor.actorId, actorName: actor.actorName });
      }

      let signedInEPortUserId = sessionStorage.getItem("ePortUserId");
      if (signedInEPortUserId != null) {
        setResponsibleEPortUserId(signedInEPortUserId);
      }
      return;
    }

    setMarketNoteId(existingMarketNote.marketNoteId);
    setOldMarketNoteId(existingMarketNote.marketNoteId);

    setTextNote(existingMarketNote.textNote);
    setOldTextNote(existingMarketNote.textNote);

    setIsDone(existingMarketNote.isDone);
    setOldIsDone(existingMarketNote.isDone);

    setNotificationDate(existingMarketNote.notificationDate ? new Date(existingMarketNote.notificationDate) : null);
    setOldNotificationDate(existingMarketNote.notificationDate ? new Date(existingMarketNote.notificationDate) : null);

    setCompany({ actorId: existingMarketNote.companyActorId, actorName: existingMarketNote.companyActorName });
    setOldCompany({ actorId: existingMarketNote.companyActorId, actorName: existingMarketNote.companyActorName });

    setContactActor({
      actorId: existingMarketNote.contactPersonActorId,
      actorName: existingMarketNote.contactPersonActorName,
    });
    setOldContactActor({
      actorId: existingMarketNote.contactPersonActorId,
      actorName: existingMarketNote.contactPersonActorName,
    });

    setResponsibleEPortUserId(existingMarketNote.responsibleEPortUserId);
    setOldResponsibleEPortUserId(existingMarketNote.responsibleEPortUserId);

    if (Array.isArray(existingMarketNote.marketNoteLabels)) {
      setSelectedMarketNoteLabelIds(existingMarketNote.marketNoteLabels.map((label) => label.marketNoteLabelId));
      setOldSelectedMarketNoteLabelIds(existingMarketNote.marketNoteLabels.map((label) => label.marketNoteLabelId));
    }
  }, [existingMarketNote]);

  useEffect(() => {
    async function fetchData() {
      await getEPortUsers();
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      await getMarketNoteLabels();
    }
    fetchData();
  }, []);

  const availableContacts = useMemo(() => {
    if (type === "company" && actor?.contacts) {
      return actor.contacts.map((c) => ({
        actorId: c.actorId,
        actorName: c.actorName,
        orgNo: c.orgNo,
        email: c.email,
      }));
    }
    return [];
  }, [actor, type]);

  const contactSearch = useCallback(
    async (searchText) => {
      if (!availableContacts || availableContacts.length === 0) {
        return [];
      }

      return availableContacts
        .filter((contact) => contact.actorName.toLowerCase().includes(searchText.toLowerCase()))
        .sort((a, b) => a.actorName.localeCompare(b.actorName));
    },
    [availableContacts],
  );

  function onContactChange(contact) {
    if (!contact) {
      // This happens if the user presses the "Enter" key,
      // rather than selecting the "Add" from the list
      setContactActor(null);
      return;
    }

    setContactActor(contact);
  }

  const availableCompanies = useMemo(() => {
    if (type === "person" && actor?.contactForCompanies) {
      return actor.contactForCompanies.map((c) => ({
        actorId: c.actorId,
        actorName: c.actorName,
      }));
    }
    return [];
  }, [actor, type]);

  const companySearch = useCallback(
    async (searchText) => {
      if (!availableCompanies || availableCompanies.length === 0) {
        return [];
      }

      return availableCompanies
        .filter((company) => company.actorName.toLowerCase().includes(searchText.toLowerCase()))
        .sort((a, b) => a.actorName.localeCompare(b.actorName));
    },
    [availableCompanies],
  );

  function handleCompanyChange(company) {
    if (!company) {
      // This happens if the user presses the "Enter" key,
      // rather than selecting the "Add" from the list
      setCompany(null);
      return;
    }

    setCompany(company);
  }

  async function getEPortUsers() {
    setIsLoading(true);

    try {
      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}eportusers`, false, "GET");
      if (response.isSuccessful) {
        const sortedUsers = [...response.ePortUsers].sort((a, b) =>
          (a.ePortUserName || "").localeCompare(b.ePortUserName || ""),
        );

        setEPortUsers(sortedUsers);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function getMarketNoteLabels() {
    setIsLoading(true);

    try {
      let response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}customerrelations/marketnote/labels`,
        false,
        "GET",
      );

      if (response.isSuccessful) {
        setMarketNoteLabels(response.marketNoteLabels);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSaveClick() {
    if (existingMarketNote != null) {
      await putMarketNote();
    } else {
      await postMarketNote();
    }
  }

  const handleLabelChange = (event) => {
    setSelectedMarketNoteLabelIds(event.target.value);
  };

  async function putMarketNote() {
    setIsLoading(true);

    try {
      let reqObj = {
        marketNoteId: marketNoteId,
        text: textNote,
        isDone: isDone,
        notificationDate: notificationDate,
        companyActorId: company.actorId,
        contactPersonActorId: contactActor?.actorId,
        responsibleEPortUserId: responsibleEPortUserId,
        marketNoteLabelIds: selectedMarketNoteLabelIds,
      };

      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}customerrelations/marketnote`, reqObj, "PUT");
      if (response.isSuccessful) {
        onSuccess();
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function postMarketNote() {
    setIsLoading(true);

    try {
      let reqObj = {
        marketNoteId: marketNoteId,
        text: textNote,
        isDone: isDone,
        notificationDate: notificationDate,
        companyActorId: company.actorId,
        contactPersonActorId: contactActor?.actorId,
        responsibleEPortUserId: responsibleEPortUserId,
        marketNoteLabelIds: selectedMarketNoteLabelIds,
      };

      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}customerrelations/marketnote`, reqObj, "POST");
      if (response.isSuccessful) {
        onSuccess();
      }
    } finally {
      setIsLoading(false);
    }
  }

  function shouldDisableDate(date) {
    const calendarDate = dayjs(date).startOf("day");
    const today = dayjs().startOf("day");

    const existingDate = existingMarketNote?.notificationDate
      ? dayjs(existingMarketNote.notificationDate).startOf("day")
      : null;

    if (existingDate && calendarDate.isSame(existingDate, "day")) {
      return false;
    }

    return calendarDate.isSame(today, "day") || calendarDate.isBefore(today, "day");
  }

  return (
    <>
      {isLoading && <Preloader />}
      <DialogTitle>
        {existingMarketNote ? Translate.get("UpdateMarketNote") : Translate.get("AddMarketNote")}
      </DialogTitle>
      <DialogContent dividers={true}>
        <Grid container spacing={2} style={{ padding: "1em" }}>
          <Grid size={{ md: 12, xs: 12 }}>
            <TextField
              value={textNote}
              fullWidth
              onChange={(e) => setTextNote(e.target.value)}
              label={Translate.get("Note")}
              multiline
              minRows={4}
            ></TextField>
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <AutocompleteSearchField
              disabled={existingMarketNote != null || (type == "company" && actor != null)}
              label={Translate.get("Company")}
              value={company}
              disableAddAsNew={true}
              onValueChange={(val) => {
                handleCompanyChange(val);
              }}
              defaultOptions={availableCompanies}
              getOptionLabel={(option) => option?.actorName ?? ""}
              renderOption={(option) => (
                <ActorAutocompleteOption
                  actorName={option?.actorName}
                  orgNo={option?.orgNo}
                  email={option?.email}
                  companies={option?.companies}
                />
              )}
              keyPropName="actorId"
              requireSelection={true}
              search={companySearch}
              textFieldProps={{ required: true }}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <AutocompleteSearchField
              disabled={existingMarketNote != null || (type == "person" && actor != null)}
              label={Translate.get("Contact")}
              value={contactActor}
              disableAddAsNew={true}
              onValueChange={(val) => {
                onContactChange(val);
              }}
              defaultOptions={availableContacts}
              getOptionLabel={(option) => option?.actorName ?? ""}
              renderOption={(option) => (
                <ActorAutocompleteOption
                  actorName={option?.actorName}
                  orgNo={option?.orgNo}
                  email={option?.email}
                  companies={option?.companies}
                />
              )}
              keyPropName="actorId"
              requireSelection={true}
              search={contactSearch}
              textFieldProps={{ required: true }}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel id="selecteportuser">{Translate.get("Responsible")}</InputLabel>
              <Select
                labelId="selecteportuser"
                value={responsibleEPortUserId}
                label={Translate.get("Responsible")}
                onChange={(e) => setResponsibleEPortUserId(e.target.value)}
              >
                {ePortUsers.map((eportUser) => (
                  <MenuItem value={eportUser.ePortUserId} key={eportUser.ePortUserId}>
                    {eportUser.ePortUserName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <DatePicker
              displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
              format={getCurrentInputFormat()}
              mask={getCurrentDateMask()}
              label={Translate.get("NotificationDate")}
              value={notificationDate}
              onChange={(newValue) => {
                setNotificationDate(newValue);
              }}
              slotProps={{ textField: { fullWidth: true } }}
              shouldDisableDate={shouldDisableDate}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox checked={Boolean(isDone)} onChange={(e) => setIsDone(e.target.checked)} color="primary" />
              }
              label={Translate.get("Ready")}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>{Translate.get("Labels")}</InputLabel>
              <Select
                multiple
                value={selectedMarketNoteLabelIds}
                onChange={handleLabelChange}
                input={<OutlinedInput label={Translate.get("Labels")} />}
                renderValue={(selectedIds) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedIds.map((id) => {
                      const label = marketNoteLabels.find((l) => l.marketNoteLabelId === id);
                      return <Chip key={id} label={label?.labelText} />;
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {marketNoteLabels.map((label) => (
                  <MenuItem key={label.marketNoteLabelId} value={label.marketNoteLabelId}>
                    {label.labelText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{Translate.get("Close")}</Button>
        <Button
          onClick={() => handleSaveClick()}
          variant="contained"
          disabled={
            textNote == "" ||
            company == null ||
            company?.actorId == "" ||
            contactActor == null ||
            contactActor?.actorId == "" ||
            responsibleEPortUserId == "" ||
            (existingMarketNote != null &&
              textNote === oldTextNote &&
              company.actorId === oldCompany.actorId &&
              contactActor.actorId === oldContactActor.actorId &&
              responsibleEPortUserId === oldResponsibleEPortUserId &&
              notificationDate?.getTime?.() === oldNotificationDate?.getTime?.() &&
              (isDone === oldIsDone || (isDone === false && oldIsDone == null)) &&
              selectedMarketNoteLabelIds.length === oldSelectedMarketNoteLabelIds.length &&
              selectedMarketNoteLabelIds.every((id) => oldSelectedMarketNoteLabelIds.includes(id)))
          }
        >
          {Translate.get("Save")}
        </Button>
      </DialogActions>
    </>
  );
};

export default AddOrUpdateMarketNote;
